/**
 * Pha D trên web quầy (docs/plans/pha-d-kho.md): thẻ "Kho" trong Sổ tiền (tồn, sắp hết, sổ kho, nhập,
 * kiểm kê), tờ "Bán lẻ", và thẻ Cấu hình "Sản phẩm" (sản phẩm + định mức theo dịch vụ).
 * Số lượng là số nguyên theo đơn vị của sản phẩm; tiền là chuỗi số nguyên đồng; máy chủ tính mọi số.
 */
import { ChevronRight, Minus, PackageOpen, Plus, ShoppingBag, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import {
  type Api,
  type DinhMuc,
  type DongSoKho,
  type HinhThucThu,
  type SanPhamKho,
  type ServiceOption,
  tienVnd,
} from './api.ts'
import { taiCoHuy } from './chi-nhanh-dang-dung.ts'
import { useChiNhanh, useChiNhanhCuaTo } from './chi-nhanh-dang-dung.tsx'
import { ChonHinhThuc, loiCua, lucViet, OLyDo, OTien, useKhoaIdem } from './GoiVi.tsx'
import { G } from './text/goi-vi.ts'
import { K } from './text/kho.ts'
import { T } from './text/vi.ts'
import { Rong, ThongBao, To, Xuong } from './ui.tsx'

/** Chỉ chữ số, tối đa `dai` chữ số → số nguyên (số lượng, không phải tiền). */
function soNguyen(s: string, dai = 7): string {
  return s.replace(/\D/g, '').slice(0, dai)
}

// ------------------------------------------------------------------ thẻ Kho (trong Sổ tiền)

export function TheKho({
  api,
  choKiemKe,
  onMoCauHinh,
}: {
  api: Api
  choKiemKe: boolean
  /** Chủ tiệm: nút ở trạng thái rỗng đưa thẳng tới Cấu hình → Sản phẩm. */
  onMoCauHinh?: () => void
}) {
  // ADR-101: kho của chi nhánh đang dùng (nút chọn chi nhánh thay `<select>` cũ ở đầu thẻ).
  const { ds: cn, id: branchId } = useChiNhanh()
  const [ds, setDs] = useState<SanPhamKho[] | null>(null)
  const [mo, setMo] = useState<SanPhamKho | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [bao, setBao] = useState<string | null>(null)

  /** Tăng lên để tải lại kho sau khi nhập / kiểm kê xong (cùng chi nhánh). */
  const [lanTai, setLanTai] = useState(0)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `branchId` là TÍN HIỆU chạy lại.
  useEffect(() => {
    // Đổi chi nhánh: đóng tờ sản phẩm đang mở — không nhập / kiểm kho nhầm sang chi nhánh khác.
    setMo(null)
    setBao(null)
    setDs(null)
  }, [branchId])
  // biome-ignore lint/correctness/useExhaustiveDependencies: `lanTai` là TÍN HIỆU tải lại.
  useEffect(() => {
    if (branchId === '') return
    // Kho của chi nhánh cũ về muộn bị bỏ (hàm dọn của taiCoHuy) — không đè danh sách chi nhánh mới (cùng mẫu ChamKhach / SoTien).
    return taiCoHuy(
      api.kho(branchId),
      (r) => setDs(r.sanPham.filter((p) => p.isActive)),
      (e) => setLoi(loiCua(e)),
    )
  }, [api, branchId, lanTai])

  if (loi !== null && ds === null) return <ThongBao loai="loi">{loi}</ThongBao>
  if (ds === null || cn === null) return <Xuong cao={200} />
  const sapHet = ds.filter((p) => p.sapHet || p.am)
  return (
    <>
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {sapHet.length > 0 && <ThongBao loai="canh-bao">{K.sapHetDauTrang(sapHet.length)}</ThongBao>}
      {ds.length === 0 ? (
        <div className="the">
          <Rong
            icon={<PackageOpen size={26} />}
            tieuDe={K.chuaCoSanPham}
            moTa={K.chuaCoSanPhamPhu}
            {...(onMoCauHinh === undefined
              ? {}
              : {
                  hanhDong: (
                    <button type="button" className="nut nut-chinh" onClick={onMoCauHinh}>
                      <Plus size={18} />
                      {K.moCauHinhSp}
                    </button>
                  ),
                })}
          />
        </div>
      ) : (
        <ul className="the ds-hoa-don">
          {[...sapHet, ...ds.filter((p) => !p.sapHet && !p.am)].map((p) => (
            <li key={p.id}>
              <button type="button" className="dong-hoa-don" onClick={() => setMo(p)}>
                <span className="chu">
                  <b>{p.name}</b>
                  {p.am ? (
                    <span className="nhan-kho nhan-kho-am">{K.am}</span>
                  ) : p.sapHet ? (
                    <span className="nhan-kho">{p.ton === '0' ? K.hetHang : K.sapHet}</span>
                  ) : (
                    <span>{p.sellPriceVnd === null ? '' : tienVnd(p.sellPriceVnd)}</span>
                  )}
                </span>
                <span className="tien">
                  <b className={p.am ? 'chu-loi' : ''}>{K.ton(p.ton, p.unit)}</b>
                </span>
                <ChevronRight size={18} className="mui-ten-dong" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {mo !== null && (
        <ToSanPhamKho
          api={api}
          branchId={branchId}
          sp={mo}
          choKiemKe={choKiemKe}
          onDong={() => setMo(null)}
          onXong={(cau) => {
            setMo(null)
            setBao(cau)
            setLanTai((n) => n + 1)
          }}
        />
      )}
    </>
  )
}

function ToSanPhamKho({
  api,
  branchId,
  sp,
  choKiemKe,
  onDong,
  onXong,
}: {
  api: Api
  branchId: string
  sp: SanPhamKho
  choKiemKe: boolean
  onDong: () => void
  onXong: (cau: string) => void
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [so, setSo] = useState<DongSoKho[] | null>(null)
  const [viec, setViec] = useState<'xem' | 'nhap' | 'kiem'>('xem')
  const [soLuong, setSoLuong] = useState('')
  const [giaNhap, setGiaNhap] = useState('')
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  /** Kiểm kê ghi sổ vĩnh viễn → hỏi lại một lần như Két (ui-critic v12). */
  const [hoi, setHoi] = useState(false)
  // Số lượng là số nguyên nhỏ (không phải tiền): chênh = đếm − sổ, hiện có dấu.
  const lechSo = soLuong === '' ? 0 : Number.parseInt(soLuong, 10) - Number.parseInt(sp.ton, 10)

  useEffect(() => {
    void api
      .soKho(branchId, sp.id)
      .then((r) => setSo(r.dong))
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api, branchId, sp.id])

  async function lam() {
    setDang(true)
    setLoi(null)
    try {
      if (viec === 'nhap') {
        await api.nhapKho(
          {
            branchId,
            productId: sp.id,
            qty: Number.parseInt(soLuong, 10),
            unitCostVnd: giaNhap === '' ? null : giaNhap,
            note: lyDo.trim() === '' ? null : lyDo.trim(),
          },
          khoa,
        )
        lamMoiKhoa()
        onXong(K.daNhap(soLuong, sp.unit))
      } else {
        const r = await api.kiemKe(
          {
            branchId,
            dem: [{ productId: sp.id, qty: Number.parseInt(soLuong, 10) }],
            note: lyDo.trim(),
          },
          khoa,
        )
        lamMoiKhoa()
        const lech = r.dieuChinh[0]?.lech
        onXong(lech === undefined ? K.khongLech : K.daKiemKe(lech, sp.unit))
      }
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  const hopLe =
    soLuong !== '' && (viec === 'nhap' ? Number.parseInt(soLuong, 10) > 0 : lyDo.trim().length >= 2)

  return (
    <To nhan={sp.name} onDong={onDong}>
      <header className="to-dau">
        <div style={{ flex: 1 }}>
          <h2>{sp.name}</h2>
          <p className="chu-phu">{K.tonHien(sp.ton, sp.unit)}</p>
        </div>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        {sp.am && <ThongBao loai="canh-bao">{K.amGiaiThich}</ThongBao>}
        {viec === 'xem' ? (
          so === null ? (
            <Xuong cao={160} />
          ) : so.length === 0 ? (
            <Rong icon={<PackageOpen size={26} />} tieuDe={K.soKhoTrong} moTa={K.soKhoTrongPhu} />
          ) : (
            <ul className="ds-so">
              {so.map((d) => (
                <li key={d.id}>
                  <span>
                    {K.loai[d.kind] ?? d.kind}
                    <span className="chu-phu">
                      {' '}
                      · {lucViet(d.luc)}
                      {d.soHoaDon !== null && ` · ${K.hoaDon(d.soHoaDon)}`}
                      {d.nguoi !== null && <span className="khong-be">{` · ${d.nguoi}`}</span>}
                      {d.note !== null && ` · ${d.note}`}
                    </span>
                  </span>
                  <b className={d.qtyDelta < 0 ? '' : 'chu-tang'}>
                    {d.qtyDelta > 0 ? '+' : '−'}
                    {Math.abs(d.qtyDelta)} {sp.unit}
                  </b>
                </li>
              ))}
            </ul>
          )
        ) : (
          <div className="muc-to">
            <div className="truong">
              <label className="nhan" htmlFor="kho-so">
                {viec === 'nhap' ? K.soLuongNhap(sp.unit) : K.demDuoc(sp.unit)}
              </label>
              <input
                id="kho-so"
                className="o-nhap o-tien"
                inputMode="numeric"
                value={K.nhom(soLuong)}
                onChange={(e) => {
                  setSoLuong(soNguyen(e.target.value))
                  setHoi(false)
                }}
              />
              {viec === 'kiem' && soLuong !== '' && (
                <p className={`chu-phu ${lechSo < 0 ? 'chu-loi' : ''}`}>
                  {K.lechXemTruoc(lechSo, sp.unit)}
                </p>
              )}
            </div>
            {viec === 'nhap' && (
              <div className="truong" style={{ marginTop: 12 }}>
                <label className="nhan" htmlFor="kho-gia">
                  {K.giaNhap(sp.unit)}
                </label>
                <OTien id="kho-gia" giaTri={giaNhap} onDoi={setGiaNhap} />
                {/* ml / g thường vài trăm đến vài nghìn đồng; từ 20.000đ trở lên nhiều khả năng gõ nhầm. */}
                {['ml', 'g', 'gram'].includes(sp.unit.trim().toLowerCase()) &&
                  giaNhap !== '' &&
                  BigInt(giaNhap) >= 20000n && (
                    <p className="chu-phu chu-canh-bao">{K.giaCao(sp.unit)}</p>
                  )}
                {giaNhap !== '' && soLuong !== '' && (
                  <p className="chu-phu dong-tong-nhap">
                    {/* Hiện tổng để bắt lỗi gõ giá cả chai vào ô giá một ml (ui-critic v15). */}
                    <b>{K.tongNhap(tienVnd((BigInt(giaNhap) * BigInt(soLuong)).toString()))}</b>
                  </p>
                )}
              </div>
            )}
            {viec === 'kiem' ? (
              <>
                <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={K.lyDoKiemKe} />
                {hoi && <ThongBao loai="canh-bao">{K.kiemKeHoiLai}</ThongBao>}
              </>
            ) : (
              <div className="truong" style={{ marginTop: 12 }}>
                <label className="nhan" htmlFor="kho-ghi-chu">
                  {K.ghiChuTuyChon}
                </label>
                <input
                  id="kho-ghi-chu"
                  className="o-nhap"
                  maxLength={300}
                  value={lyDo}
                  onChange={(e) => setLyDo(e.target.value)}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <footer className="to-chan">
        {viec === 'xem' ? (
          <>
            {choKiemKe && (
              <button type="button" className="nut nut-vien" onClick={() => setViec('kiem')}>
                {K.kiemKe}
              </button>
            )}
            <button
              type="button"
              className="nut nut-chinh nut-lon"
              style={{ flex: 1 }}
              onClick={() => setViec('nhap')}
            >
              {K.nhapHang}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="nut nut-vien"
              onClick={() => {
                setViec('xem')
                setHoi(false)
              }}
            >
              {G.back}
            </button>
            <button
              type="button"
              className="nut nut-chinh nut-lon"
              style={{ flex: 1 }}
              disabled={!hopLe || dang}
              onClick={() => (viec === 'kiem' && !hoi ? setHoi(true) : void lam())}
            >
              {viec === 'nhap'
                ? K.nhapNut(soLuong === '' ? '0' : soLuong, sp.unit)
                : hoi
                  ? K.kiemKeThat
                  : K.kiemKeNut}
            </button>
          </>
        )}
      </footer>
    </To>
  )
}

// ------------------------------------------------------------------ chọn sản phẩm bán lẻ (dùng chung)

/** Danh sách sản phẩm bán lẻ với nút −/+; trả về map productId → số lượng. */
export function ChonSanPham({
  ds,
  chon,
  onDoi,
}: {
  ds: SanPhamKho[]
  chon: Record<string, number>
  onDoi: (m: Record<string, number>) => void
}) {
  const ban = ds.filter((p) => p.isActive && p.sellPriceVnd !== null)
  if (ban.length === 0) {
    return <Rong icon={<ShoppingBag size={26} />} tieuDe={K.chuaBanLe} moTa={K.chuaBanLePhu} />
  }
  return (
    <ul className="ds-so">
      {ban.map((p) => {
        const n = chon[p.id] ?? 0
        return (
          <li key={p.id}>
            <span className="chu-sp">
              <span>{p.name}</span>
              <span
                className={`chu-phu ${p.ton.startsWith('-') || p.ton === '0' ? 'chu-loi' : ''}`}
              >
                {tienVnd(p.sellPriceVnd ?? '0')} ·{' '}
                {p.ton.startsWith('-') || p.ton === '0' ? K.hetHangBan : K.conTon(p.ton, p.unit)}
              </span>
            </span>
            <span className="bo-dem">
              <button
                type="button"
                className="nut nut-vien nut-icon"
                aria-label={K.bot(p.name)}
                disabled={n === 0}
                onClick={() => onDoi({ ...chon, [p.id]: n - 1 })}
              >
                <Minus size={18} />
              </button>
              <b>{n}</b>
              <button
                type="button"
                className="nut nut-vien nut-icon"
                aria-label={K.them(p.name)}
                onClick={() => onDoi({ ...chon, [p.id]: n + 1 })}
              >
                <Plus size={18} />
              </button>
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/** Tổng tiền các sản phẩm đã chọn (chỉ HIỂN THỊ — máy chủ tính lại theo giá của nó). */
export function tongSanPham(ds: SanPhamKho[], chon: Record<string, number>): bigint {
  return ds.reduce(
    (t, p) => t + (p.sellPriceVnd === null ? 0n : BigInt(p.sellPriceVnd) * BigInt(chon[p.id] ?? 0)),
    0n,
  )
}

export function dsChon(chon: Record<string, number>): { productId: string; qty: number }[] {
  return Object.entries(chon)
    .filter(([, n]) => n > 0)
    .map(([productId, qty]) => ({ productId, qty }))
}

// ------------------------------------------------------------------ tờ Bán lẻ

export function ToBanLe({
  api,
  onDong,
  onXong,
}: {
  api: Api
  onDong: () => void
  onXong: (cau: string) => void
}) {
  // Tiệm nhiều chi nhánh: chọn chi nhánh bán để trừ đúng kho (ui-critic v12) — mặc định chi nhánh đang dùng (ADR-101).
  const { ds: dsChiNhanh, nhieu, branchId, setBranchId } = useChiNhanhCuaTo()
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [ds, setDs] = useState<SanPhamKho[] | null>(null)
  const [chon, setChon] = useState<Record<string, number>>({})
  const [hinhThuc, setHinhThuc] = useState<HinhThucThu>('cash')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  useEffect(() => {
    setDs(null)
    setChon({})
    // Đổi chi nhánh bán giữa chừng: kho của chi nhánh cũ về muộn bị bỏ, không đè danh sách (và giá / tồn) của chi nhánh mới.
    return taiCoHuy(
      api.kho(branchId),
      (r) => setDs(r.sanPham),
      (e) => setLoi(loiCua(e)),
    )
  }, [api, branchId])
  const tong = ds === null ? 0n : tongSanPham(ds, chon)
  async function thu() {
    setDang(true)
    setLoi(null)
    try {
      const r = await api.banLe(
        {
          branchId,
          sanPham: dsChon(chon),
          hinhThuc,
          giamVnd: '0',
          lyDoGiam: null,
          duKienVnd: tong.toString(),
        },
        khoa,
      )
      lamMoiKhoa()
      onXong(K.daBanLe(r.hoaDon.number, tienVnd(r.hoaDon.totalVnd)))
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  return (
    <To nhan={K.banLeTitle} onDong={onDong}>
      <header className="to-dau">
        <div style={{ flex: 1 }}>
          <h2>{K.banLeTitle}</h2>
        </div>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        {nhieu && (
          <select
            className="o-chon"
            aria-label={T.branchLabel}
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {dsChiNhanh.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
        {ds === null ? <Xuong cao={160} /> : <ChonSanPham ds={ds} chon={chon} onDoi={setChon} />}
        {tong > 0n && <ChonHinhThuc giaTri={hinhThuc} onDoi={setHinhThuc} />}
        {tong > 0n && (
          <p className="dong-phai-tra">
            <span>{K.tongBanLe}</span>
            <b>{tienVnd(tong.toString())}</b>
          </p>
        )}
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={tong === 0n || dang}
          onClick={() => void thu()}
        >
          {tong === 0n
            ? K.chonSanPham
            : K.thuBanLe(tienVnd(tong.toString()), G.hinhThuc[hinhThuc] ?? '')}
        </button>
      </footer>
    </To>
  )
}

// ------------------------------------------------------------------ Cấu hình: sản phẩm + định mức

type NhapSp = {
  id: string | null
  name: string
  unit: string
  gia: string
  minQty: string
  isActive: boolean
}

export function TheSanPhamCauHinh({ api }: { api: Api }) {
  const [ds, setDs] = useState<SanPhamKho[] | null>(null)
  const [dv, setDv] = useState<ServiceOption[]>([])
  const [dm, setDm] = useState<DinhMuc[]>([])
  const [sua, setSua] = useState<NhapSp | null>(null)
  const [dvChon, setDvChon] = useState('')
  const [dongDm, setDongDm] = useState<{ productId: string; qty: string }[]>([])
  const [bao, setBao] = useState<string | null>(null)
  const [loi, setLoi] = useState<string | null>(null)

  const nap = useCallback(async () => {
    try {
      const [k, s, d] = await Promise.all([api.kho(null), api.services(), api.dinhMuc()])
      setDs(k.sanPham)
      setDv(s.services)
      setDm(d.dinhMuc)
    } catch (e) {
      setLoi(loiCua(e))
    }
  }, [api])
  useEffect(() => {
    void nap()
  }, [nap])

  async function luuSp() {
    if (sua === null) return
    try {
      await api.luuSanPham(sua.id, {
        name: sua.name.trim(),
        unit: sua.unit.trim(),
        sellPriceVnd: sua.gia === '' ? null : sua.gia,
        minQty: sua.minQty === '' ? 0 : Number.parseInt(sua.minQty, 10),
        isActive: sua.isActive,
      })
      setSua(null)
      setBao(K.daLuuSp)
      await nap()
    } catch (e) {
      setLoi(loiCua(e))
    }
  }

  function chonDv(id: string) {
    setDvChon(id)
    setDongDm(
      dm
        .filter((d) => d.serviceVariantId === id)
        .map((d) => ({ productId: d.productId, qty: String(d.qty) })),
    )
  }

  async function luuDm() {
    try {
      const r = await api.luuDinhMuc({
        serviceVariantId: dvChon,
        dong: dongDm
          .filter((d) => d.productId !== '' && d.qty !== '' && Number.parseInt(d.qty, 10) > 0)
          .map((d) => ({ productId: d.productId, qty: Number.parseInt(d.qty, 10) })),
      })
      setDm(r.dinhMuc)
      setBao(K.daLuuDm)
    } catch (e) {
      setLoi(loiCua(e))
    }
  }

  const spDung = (ds ?? []).filter((p) => p.isActive)
  const hopLeSp =
    sua !== null &&
    sua.name.trim() !== '' &&
    sua.unit.trim() !== '' &&
    (sua.gia === '' || (sua.gia.endsWith('000') && sua.gia !== '0'))

  return (
    <div className="goi-vi-cau-hinh">
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && (
        <ThongBao loai="loi" onDong={() => setLoi(null)}>
          {loi}
        </ThongBao>
      )}
      <section className="the the-dem">
        <div className="dau-muc">
          <div>
            <h2>{K.spTitle}</h2>
            <p className="chu-phu">{K.spHint}</p>
          </div>
          <button
            type="button"
            className="nut nut-vien nut-nho"
            disabled={sua !== null}
            onClick={() =>
              setSua({ id: null, name: '', unit: '', gia: '', minQty: '', isActive: true })
            }
          >
            <Plus size={16} />
            {K.themSp}
          </button>
        </div>
        {ds === null ? (
          <Xuong cao={60} />
        ) : (
          <ul className="ds-goi-mau">
            {ds.map((p) => (
              <li key={p.id} className={p.isActive ? '' : 'tat'}>
                <button
                  type="button"
                  className="dong-goi-mau"
                  onClick={() =>
                    setSua({
                      id: p.id,
                      name: p.name,
                      unit: p.unit,
                      gia: p.sellPriceVnd ?? '',
                      minQty: p.minQty === 0 ? '' : String(p.minQty),
                      isActive: p.isActive,
                    })
                  }
                >
                  <span className="chu">
                    <b>{p.name}</b>
                    <span>
                      {p.unit}
                      {p.minQty > 0 && ` · ${K.canhBao} ${p.minQty}`}
                    </span>
                  </span>
                  <span className="gia">
                    {p.sellPriceVnd === null ? '' : tienVnd(p.sellPriceVnd)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {sua !== null && (
          <div className="form-goi">
            <h3>{sua.id === null ? K.themSp : K.suaSp}</h3>
            <div className="truong">
              <label className="nhan" htmlFor="sp-ten">
                {K.tenSp}
              </label>
              <input
                id="sp-ten"
                className="o-nhap"
                placeholder={K.tenSpGoiY}
                maxLength={120}
                value={sua.name}
                onChange={(e) => setSua({ ...sua, name: e.target.value })}
              />
            </div>
            <div className="luoi-2">
              <div className="truong">
                <label className="nhan" htmlFor="sp-dv">
                  {K.donVi}
                </label>
                <input
                  id="sp-dv"
                  className="o-nhap"
                  placeholder={K.donViGoiY}
                  maxLength={20}
                  value={sua.unit}
                  onChange={(e) => setSua({ ...sua, unit: e.target.value })}
                />
              </div>
              <div className="truong">
                <label className="nhan" htmlFor="sp-min">
                  {K.canhBao}
                </label>
                <input
                  id="sp-min"
                  className="o-nhap"
                  inputMode="numeric"
                  value={sua.minQty}
                  onChange={(e) => setSua({ ...sua, minQty: soNguyen(e.target.value) })}
                />
              </div>
            </div>
            <div className="truong">
              <label className="nhan" htmlFor="sp-gia">
                {K.giaBan}
              </label>
              <OTien id="sp-gia" giaTri={sua.gia} onDoi={(v) => setSua({ ...sua, gia: v })} />
            </div>
            <label className="cong-tac-dong">
              <span className="chu">
                <b>{K.dangDung}</b>
              </span>
              <input
                type="checkbox"
                checked={sua.isActive}
                onChange={(e) => setSua({ ...sua, isActive: e.target.checked })}
              />
            </label>
            <div className="hang-chip">
              <button type="button" className="nut nut-nhat" onClick={() => setSua(null)}>
                {T.cancel}
              </button>
              <button
                type="button"
                className="nut nut-chinh"
                disabled={!hopLeSp}
                onClick={() => void luuSp()}
              >
                {K.luuSp}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="the the-dem">
        <h2>{K.dmTitle}</h2>
        <p className="chu-phu">{K.dmHint}</p>
        <select
          className="o-chon"
          aria-label={K.chonDv}
          style={{ marginTop: 12 }}
          value={dvChon}
          onChange={(e) => chonDv(e.target.value)}
        >
          <option value="">{K.chonDv}</option>
          {dv.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        {dvChon !== '' && (
          <>
            <div className="ds-muc-tang">
              {dongDm.map((d, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: dòng nhập chưa có id; thứ tự là danh tính.
                <div key={i} className="dong-muc-tang">
                  <div className="truong">
                    <select
                      className="o-chon"
                      aria-label={K.tenSp}
                      value={d.productId}
                      onChange={(e) =>
                        setDongDm(
                          dongDm.map((x, j) => (j === i ? { ...x, productId: e.target.value } : x)),
                        )
                      }
                    >
                      <option value="">{K.chonSp}</option>
                      {spDung.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.unit})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="truong o-co-don-vi">
                    <input
                      className="o-nhap"
                      inputMode="numeric"
                      aria-label={K.soLuong}
                      placeholder={K.soLuong}
                      value={d.qty}
                      onChange={(e) =>
                        setDongDm(
                          dongDm.map((x, j) =>
                            j === i ? { ...x, qty: soNguyen(e.target.value, 6) } : x,
                          ),
                        )
                      }
                    />
                    <span className="don-vi">
                      {spDung.find((p) => p.id === d.productId)?.unit ?? ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="nut nut-nhat nut-icon"
                    aria-label={K.boDong}
                    onClick={() => setDongDm(dongDm.filter((_, j) => j !== i))}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
            <div className="hang-chip" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="nut nut-vien nut-nho"
                onClick={() => setDongDm([...dongDm, { productId: '', qty: '' }])}
              >
                <Plus size={16} />
                {K.themDong}
              </button>
              <button type="button" className="nut nut-chinh nut-nho" onClick={() => void luuDm()}>
                {K.luuDm}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
